import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { navigate, setLeaveGuard } from '../boot/router';
import { canEdit, type AppUser } from '../boot/session';
import { isDevPreview } from '../boot/devPreview';
import { onReservedKey } from '../boot/keyboard';
import { loadDrawing, saveDocument, type LoadedDrawing } from '../data/drawingRepo';
import { registerDevDocument } from '../data/versionRepo';
import { blockRowToCatalogBlock, listBlocks } from '../data/blockRepo';
import { VERSION_STATUS_META } from '../data/types';
import { useAsync } from '../hooks/useAsync';
import { ErrorState, LoadingState } from '../shell/State';
import { OneUI } from '../shell/oneUi';
import { Scene } from '../cad/scene';
import { Editor } from '../cad/editor';
import { CadCanvas } from '../cad/ui/Canvas';
import { CommandLine } from '../cad/ui/CommandLine';
import { SidePanel } from '../cad/ui/SidePanel';
import { StatusBar } from '../cad/ui/StatusBar';
import { Toolbar } from '../cad/ui/Toolbar';
import { FileMenu } from '../cad/ui/FileMenu';
import { Timeline } from '../cad/ui/Timeline';
import { PresenceBar } from '../cad/ui/PresenceBar';
import '../cad/ui/editor.css';

/**
 * 도면 편집기 화면 (작업지시서 P2 전체)
 *
 * 이 컴포넌트가 하는 일:
 *   1. 도면 본문을 불러와 Scene · Editor 를 만든다
 *   2. 전역 단축키를 Editor 동작으로 잇는다 (P2-14)
 *   3. 자동 저장 — 10초 디바운스 + 명령 완료 시 (P2-18)
 *   4. 미저장 상태로 화면을 떠나려 하면 막는다
 */
export function EditorPage({
  drawingId,
  versionId,
  user,
  readOnly = false,
}: {
  drawingId: string;
  versionId?: string;
  user: AppUser;
  readOnly?: boolean;
}) {
  const loaded = useAsync<LoadedDrawing>(
    () => loadDrawing(drawingId, versionId),
    [drawingId, versionId]
  );

  if (loaded.loading) return <LoadingState label="도면을 불러오는 중…" />;
  if (loaded.error) {
    return (
      <div className="page">
        <ErrorState
          title="도면을 열 수 없습니다"
          message={loaded.error}
          onRetry={loaded.reload}
        />
      </div>
    );
  }
  if (!loaded.data) return null;

  return (
    <EditorShell
      key={`${drawingId}:${versionId ?? 'latest'}`}
      loaded={loaded.data}
      user={user}
      forceReadOnly={readOnly}
    />
  );
}

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: string }
  | { kind: 'error'; message: string };

function EditorShell({
  loaded,
  user,
  forceReadOnly,
}: {
  loaded: LoadedDrawing;
  user: AppUser;
  forceReadOnly: boolean;
}) {
  const editor = useMemo(() => new Editor(new Scene(loaded.document)), [loaded]);
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  const [dirty, setDirty] = useState(false);
  // 검토 패널이 상태를 바꾸면 머리말과 편집 가능 여부가 즉시 따라가야 한다
  const [status, setStatus] = useState(loaded.version.status);
  const [timelineOpen, setTimelineOpen] = useState(false);

  // 편집 가능 여부 = 버전 상태 ∧ 시스템 역할 ∧ 화면 모드 ∧ 되감기 중 아님
  const editable =
    status !== 'approved' &&
    status !== 'published' &&
    canEdit(user) &&
    !forceReadOnly &&
    !timelineOpen;
  const saveTimer = useRef<number | null>(null);
  const saving = useRef(false);
  /** 연속 저장 실패 횟수 — 재시도 간격을 늘리는 데 쓴다 */
  const failCount = useRef(0);

  /* ------------------------------------------------------ 초기 화면 */

  useEffect(() => {
    editor.write(`${loaded.drawing.name} · v${loaded.version.version_number} 열림`);
    if (!editable) {
      editor.write('읽기 전용입니다. 승인된 버전이거나 편집 권한이 없습니다.');
    }
    // 미리보기 모드에서 비교 화면이 읽을 수 있도록 본문을 등록한다
    registerDevDocument(loaded.version.id, editor.scene.doc);

    // 로컬 개발 미리보기에서만 편집기 인스턴스를 노출한다 (디버깅·자동 검증용)
    if (isDevPreview()) {
      (window as unknown as { __cadEditor?: unknown }).__cadEditor = editor;
    }
  }, [editor, loaded, editable]);

  // 도면에서 등록한 커스텀 블록(P4-06 확장)을 카탈로그에 얹는다 — 표준 60종은
  // 이미 코드에 있으니, 여기서는 도형이 있는(geometry_json 채워진) 행만 본다.
  useEffect(() => {
    let alive = true;
    void listBlocks()
      .then((rows) => {
        if (!alive) return;
        for (const row of rows) {
          const block = blockRowToCatalogBlock(row);
          if (block) editor.addCustomBlock(block);
        }
      })
      .catch(() => {
        /* 커스텀 블록을 못 읽어도 표준 60종으로는 계속 작업할 수 있어야 한다 */
      });
    return () => {
      alive = false;
    };
  }, [editor]);

  /**
   * 화면 맞춤은 캔버스가 실제 크기를 얻은 뒤에 해야 한다.
   * 여기(부모 effect)에서 바로 부르면 레이아웃 확정 전이라 배율이 틀어진다.
   */
  const handleCanvasReady = useCallback(() => {
    editor.zoomExtents();
  }, [editor]);

  /* ------------------------------------------------------- 자동 저장 */

  const flush = useCallback(async () => {
    if (!editable || saving.current) return;
    if (!editor.scene.isDirty) return;

    const batches = editor.scene.takePending();
    if (batches.length === 0) return;

    saving.current = true;
    setSave({ kind: 'saving' });
    try {
      const r = await saveDocument(loaded.version.id, editor.scene.doc, batches);
      failCount.current = 0;
      setSave({ kind: 'saved', at: r.at });
      setDirty(false);
    } catch (e) {
      // 유실 0 — 실패하면 되돌려서 다음 기회에 다시 저장한다
      editor.scene.restorePending(batches);
      failCount.current += 1;
      setSave({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
      setDirty(true);
    } finally {
      saving.current = false;
    }
  }, [editable, editor, loaded.version.id]);

  useEffect(() => {
    if (!editable) return;

    /*
     * 명령이 끝날 때마다 pending 이 쌓인다 → 10초 디바운스로 묶어 저장.
     *
     * ★ 실패하면 간격을 늘린다(10초 → 20 → 40 … 최대 2분). 예전에는 실패해도
     *   10초마다 그대로 다시 던져서, 큰 도면(수 MB)일 때 서버를 계속 두드렸다.
     *   실제로 Supabase 프로젝트가 응답을 멈춰 재시작해야 했다.
     */
    const unsub = editor.scene.subscribe(() => {
      setDirty(true);
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      const delay = Math.min(10_000 * 2 ** failCount.current, 120_000);
      saveTimer.current = window.setTimeout(() => void flush(), delay);
    });

    return () => {
      unsub();
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [editable, editor, flush]);

  // 탭을 닫거나 새로고침할 때 브라우저 기본 경고
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (editor.scene.isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [editor]);

  // 화면 이동(해시 변경) 가드 — ONE 쉘이 hash 만 바꿔도 걸린다
  useEffect(() => {
    setLeaveGuard(async () => {
      if (!editor.scene.isDirty) return true;
      await flush();
      if (!editor.scene.isDirty) return true;
      return OneUI.confirm('나가면 저장하지 못한 변경은 사라집니다.', {
        title: '저장하지 못한 변경이 있습니다. 그래도 나갈까요?',
        okText: '나가기',
        cancelText: '머무르기',
        danger: true,
      });
    });
    return () => setLeaveGuard(null);
  }, [editor, flush]);

  /* --------------------------------------------------------- 단축키 */

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // 대화상자·모달이 떠 있으면 편집기 단축키를 쉰다. 안 그러면 확인창 버튼의
      // Enter 가 명령 확정으로 먹히고, 모달 안 Delete·Ctrl+Z 가 도면을 지우거나 되돌린다.
      if (OneUI.hasOpen()) return;
      const target = e.target as HTMLElement | null;
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable === true;

      // 기능키와 Ctrl 조합은 입력창 안에서도 동작해야 한다
      switch (e.key) {
        case 'F3':
          e.preventDefault();
          editor.toggleOsnap();
          return;
        case 'F8':
          e.preventDefault();
          editor.toggleOrtho();
          return;
        case 'F9':
          e.preventDefault();
          editor.toggleGridSnap();
          return;
        case 'F10':
          e.preventDefault();
          editor.togglePolar();
          return;
      }

      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl) {
        const k = e.key.toLowerCase();
        if (k === 'z') {
          e.preventDefault();
          if (editable) (e.shiftKey ? editor.redo() : editor.undo());
          else editor.write('읽기 전용입니다. 편집 권한이 있는 버전에서 다시 시도하세요.');
          return;
        }
        if (k === 'y') {
          e.preventDefault();
          if (editable) editor.redo();
          else editor.write('읽기 전용입니다. 편집 권한이 있는 버전에서 다시 시도하세요.');
          return;
        }
        if (k === 's') {
          e.preventDefault();
          void flush();
          return;
        }
        if (k === 'a') {
          // 명령행 입력창은 이 앱에서 기본으로 포커스를 차지하는 곳이라,
          // 다른 텍스트 필드(댓글·이름변경 등)처럼 "글자 전체선택"을 우선시키면
          // Ctrl+A 가 거의 항상 먹통으로 보인다. 명령행만 예외로 둔다.
          const isCmdline =
            target instanceof HTMLElement && target.dataset.cmdline === 'true';
          if (typing && !isCmdline) return; // 일반 텍스트 필드는 네이티브 전체선택 유지
          e.preventDefault();
          editor.selectAll();
          return;
        }
        return;
      }

      // 아래부터는 입력창에 타이핑 중이면 건드리지 않는다
      if (typing) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (editable) editor.deleteSelection();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        editor.feed({ t: 'esc' });
        return;
      }
      // 명령행이 아니라 캔버스(또는 그 주변)에 포커스가 있을 때의 Enter.
      // 명령행에 포커스가 있을 때는 CommandLine.tsx 의 onKeyDown 이 이미 처리하므로
      // (target 이 텍스트 입력이면 typing===true 라 여기까지 오지 않는다) 겹치지 않는다.
      if (e.key === 'Enter') {
        e.preventDefault();
        editor.feed({ t: 'enter' });
      }
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [editor, editable, flush]);

  /**
   * 맨 Space(조합키 없음)는 `installGlobalKeyGuards()` 가 브라우저 기본 스크롤을
   * 막으려고 전역에서 선점해 버린다(입력창 안에서는 그 가드가 스스로 비켜준다).
   * 그런데 그 자리를 실제로 채우는 코드가 어디에도 없어서, 캔버스에 포커스가
   * 있을 때는 Space 를 눌러도 아무 일도 안 일어났다 — "우클릭은 되는데 Space 는
   * 안 된다"는 것과 정확히 일치하는 증상이다. 매뉴얼대로 Enter 와 동일하게
   * "지금 하는 것을 끝낸다"로 채운다.
   */
  useEffect(() => onReservedKey((e) => {
    if (e.key !== ' ') return false;
    editor.feed({ t: 'enter' });
    return true;
  }), [editor]);

  /* ------------------------------------------------------------ 화면 */

  const meta = VERSION_STATUS_META[status];

  return (
    <div className="cad-editor">
      <header className="cad-editor__head">
        <button
          type="button"
          className="cad-btn"
          onClick={() => navigate({ name: 'project', projectId: loaded.drawing.project_id })}
          title="프로젝트로 돌아가기"
        >
          ← 프로젝트
        </button>

        <h1 className="cad-editor__title">{loaded.drawing.name}</h1>

        <span className="cad-editor__meta">
          v{loaded.version.version_number} · <span aria-hidden="true">{meta.icon}</span>{' '}
          {meta.label}
          {loaded.drawing.floor_name && ` · ${loaded.drawing.floor_name}`}
        </span>

        <PresenceBar
          editor={editor}
          versionId={loaded.version.id}
          me={{ email: user.email, name: user.name ?? user.email }}
        />

        <span className="cad-editor__spacer" />

        {!editable && (
          <span className="cad-editor__meta" title="승인된 버전이거나 편집 권한이 없습니다">
            👁 읽기 전용
          </span>
        )}

        <FileMenu
          editor={editor}
          drawingName={loaded.drawing.name}
          versionLabel={`v${loaded.version.version_number}`}
          editable={editable}
          isDraft={loaded.version.status !== 'approved' && loaded.version.status !== 'published'}
          projectId={loaded.drawing.project_id}
          drawingId={loaded.drawing.id}
          versionId={loaded.version.id}
        />

        <button
          type="button"
          className={timelineOpen ? 'cad-btn cad-btn--on' : 'cad-btn'}
          aria-pressed={timelineOpen}
          onClick={() => {
            // 되감는 동안은 편집을 막는다 — 과거 시점에서 그리면 이후 작업이 날아간다
            if (!timelineOpen) void flush();
            setTimelineOpen((v) => !v);
          }}
          title="작업 되감기 (누가 언제 무엇을 했는지)"
        >
          ⏱ 이력
        </button>

        {editable && <SaveBadge state={save} dirty={dirty} />}

        <button
          type="button"
          className="cad-btn"
          disabled={!editable || !editor.snapshot().canUndo}
          onClick={() => editor.undo()}
          title="실행 취소 (Ctrl+Z)"
        >
          ↶ 취소
        </button>
        <button
          type="button"
          className="cad-btn"
          disabled={!editable || !editor.snapshot().canRedo}
          onClick={() => editor.redo()}
          title="다시 실행 (Ctrl+Y)"
        >
          ↷ 다시
        </button>
        <button
          type="button"
          className="cad-btn cad-btn--primary"
          disabled={!editable || save.kind === 'saving'}
          onClick={() => void flush()}
          title="지금 저장 (Ctrl+S)"
        >
          {save.kind === 'saving' ? '저장 중…' : '저장'}
        </button>
      </header>

      <Toolbar editor={editor} />
      <CadCanvas editor={editor} onReady={handleCanvasReady} />
      <SidePanel
        editor={editor}
        context={{
          projectId: loaded.drawing.project_id,
          drawingId: loaded.drawing.id,
          drawingName: loaded.drawing.name,
          versionId: loaded.version.id,
          branchId: loaded.version.branch_id,
          status,
          user,
          onStatusChange: setStatus,
          // 버전을 바꾸면 편집기를 통째로 다시 만든다 (key 가 바뀐다).
          // 열려 있던 도면을 재사용하면 다른 버전 본문이 섞인다.
          onNavigateVersion: (id) =>
            navigate({ name: 'version', drawingId: loaded.drawing.id, versionId: id }),
        }}
      />
      {timelineOpen && (
        <Timeline
          versionId={loaded.version.id}
          scene={editor.scene}
          onClose={() => setTimelineOpen(false)}
        />
      )}
      <CommandLine editor={editor} />
      <StatusBar editor={editor} />
    </div>
  );
}

function SaveBadge({ state, dirty }: { state: SaveState; dirty: boolean }) {
  if (state.kind === 'saving') {
    return <span className="cad-save-state">저장 중…</span>;
  }
  if (state.kind === 'error') {
    return (
      <span className="cad-save-state cad-save-state--error" title={state.message}>
        ⚠ 저장 실패 — 변경은 보존됨
      </span>
    );
  }
  if (dirty) {
    return <span className="cad-save-state cad-save-state--dirty">● 저장되지 않음</span>;
  }
  if (state.kind === 'saved') {
    return (
      <span className="cad-save-state">
        ✓ {new Date(state.at).toLocaleTimeString('ko-KR')} 저장됨
      </span>
    );
  }
  return <span className="cad-save-state">변경 없음</span>;
}
